import { staticError, useGuardedEffect } from "@p0security/frontend-core";
import {
  InventoryItem,
  countNodes,
} from "@p0security/shared/assessment/inventory";
import { dslParse as parser } from "@p0security/shared/graph/dsl/index";
import { DirectedGraph } from "@p0security/shared/graph/types";
import {
  AssessmentNodes,
  TargetNodeType,
} from "@p0security/shared/types/assessment/data/index";
import { useContext, useState } from "react";

import { ScopeContext } from "../contexts/ScopeContext";
import { useFetchInventoryCounts } from "./useFetchInventoryCounts";

type CountMap = Record<string, number>;

export const makeKey = (l: InventoryItem<TargetNodeType>) =>
  `${l.show}:${l.where}`;

export const useInventoryCounts = (
  links: InventoryItem<TargetNodeType>[],
  graph: DirectedGraph<AssessmentNodes> | undefined
) => {
  const { doc: allInventoryCounts, loading: snapshotLoading } =
    useFetchInventoryCounts();
  const { provider } = useContext(ScopeContext);
  const [counts, setCounts] = useState<CountMap>({});
  const [loading, setLoading] = useState(true);

  useGuardedEffect(
    (cancellation) => async () => {
      if (!graph || snapshotLoading) return;

      if (cancellation.isCancelled) return;

      if (provider === "all" && allInventoryCounts) {
        const fromSnapshot: CountMap = Object.fromEntries(
          links.map((l) => [
            makeKey(l),
            allInventoryCounts?.[l.show]?.[l.label] ?? 0,
          ])
        );

        setCounts(fromSnapshot);
        setLoading(false);

        return;
      }

      await Promise.all(
        links.map(async (l) => {
          const key = makeKey(l);
          const n = (await countNodes({ link: l, graph, parser })).nodes.length;
          if (!cancellation.isCancelled) {
            setCounts((prev) => ({ ...prev, [key]: n }));
          }
        })
      );
      if (!cancellation.isCancelled) {
        setLoading(false);
      }
    },
    [graph, links, provider, snapshotLoading, allInventoryCounts],
    staticError
  );

  return { counts, loading };
};
